// Geometría del recortador del logo de la actividad (LogoCropper.vue).
//
// El logo se guarda siempre en 950 × 400 px: la proporción del recuadro del logo en el RIDE
// (9,5 × 4 cm), que así lo llena entero sin deformarlo. El backend exige esa medida
// (`backend/logos.py` encaja ahí lo que llegue de otra). Todo se calcula en
// píxeles del logo final: la imagen se dibuja escalada `scale` veces con su esquina en (x, y).
// Se puede alejar hasta que entre entera (lo que sobra queda transparente) o acercar para recortar.

export const LOGO_WIDTH = 950;
export const LOGO_HEIGHT = 400;
// Cuánto se puede acercar, sobre la escala que llena el recuadro.
export const MAX_ZOOM = 4;

// Escala con la que la imagen entra entera en el recuadro.
export function containScale(width, height) {
    return Math.min(LOGO_WIDTH / width, LOGO_HEIGHT / height);
}

// Escala con la que la imagen llena el recuadro (recortando lo que sobre).
export function coverScale(width, height) {
    return Math.max(LOGO_WIDTH / width, LOGO_HEIGHT / height);
}

export function scaleLimits(width, height) {
    return { min: containScale(width, height), max: coverScale(width, height) * MAX_ZOOM };
}

function clampAxis(position, drawn, frame) {
    // Más grande que el recuadro: no deja huecos. Más chica: no se sale de él.
    const low = Math.min(0, frame - drawn);
    const high = Math.max(0, frame - drawn);
    return Math.min(high, Math.max(low, position));
}

export function clamp(state, width, height) {
    const limits = scaleLimits(width, height);
    const scale = Math.min(limits.max, Math.max(limits.min, state.scale));
    return {
        scale,
        x: clampAxis(state.x, width * scale, LOGO_WIDTH),
        y: clampAxis(state.y, height * scale, LOGO_HEIGHT),
    };
}

// La imagen centrada a una escala dada.
export function centered(scale, width, height) {
    return clamp({ scale, x: (LOGO_WIDTH - width * scale) / 2, y: (LOGO_HEIGHT - height * scale) / 2 }, width, height);
}

// Arrastrar: mueve en píxeles del logo final.
export function pan(state, dx, dy, width, height) {
    return clamp({ scale: state.scale, x: state.x + dx, y: state.y + dy }, width, height);
}

// Acercar o alejar dejando quieto el punto (ax, ay) del recuadro (el puntero, o el centro).
export function zoomTo(state, scale, width, height, ax = LOGO_WIDTH / 2, ay = LOGO_HEIGHT / 2) {
    const ratio = scale / state.scale;
    return clamp({ scale, x: ax - (ax - state.x) * ratio, y: ay - (ay - state.y) * ratio }, width, height);
}

// Llena todo el recuadro: entonces una foto opaca puede ir en JPEG, que pesa mucho menos.
export function coversFrame(state, width, height) {
    return state.x <= 0 && state.y <= 0
        && state.x + width * state.scale >= LOGO_WIDTH
        && state.y + height * state.scale >= LOGO_HEIGHT;
}

// Formato del logo recortado: PNG si hay márgenes transparentes o el original puede tener
// transparencia; JPEG solo para una foto opaca que llena el recuadro.
export function outputType(fileType, covers) {
    const mayHaveAlpha = ['image/png', 'image/webp', 'image/gif', 'image/svg+xml'].includes(fileType);
    return covers && !mayHaveAlpha ? 'image/jpeg' : 'image/png';
}

// Nombre del archivo recortado, con la extensión de su formato.
export function croppedName(originalName, type) {
    const base = String(originalName || 'logo').replace(/\.[^.]+$/, '') || 'logo';
    return base + (type === 'image/jpeg' ? '.jpg' : '.png');
}
